import React, { useEffect, useRef } from "react";

const categories = [
  { 
    title: "Adoption", 
    image: "/assets/adoption_cat.jpg",
    gradient: "from-amber-500 to-orange-500",
    lightBg: "bg-amber-50",
    pattern: "bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-100/40 via-amber-50/20 to-transparent",
    description: "Give a child a loving home"
  },
  { 
    title: "Animals & Pets", 
    image: "/assets/animals_cat.jpg",
    gradient: "from-teal-500 to-emerald-500",
    lightBg: "bg-teal-50",
    pattern: "bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-teal-100/40 via-teal-50/20 to-transparent",
    description: "Rescue, care, and protect"
  },
  { 
    title: "Business", 
    image: "/assets/business (2)_cat.jpg",
    gradient: "from-blue-500 to-indigo-500",
    lightBg: "bg-blue-50",
    pattern: "bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-blue-100/40 via-blue-50/20 to-transparent",
    description: "Turn ideas into reality"
  },
  { 
    title: "Competitive", 
    image: "/assets/competitive_cat.jpg",
    gradient: "from-violet-500 to-purple-500",
    lightBg: "bg-violet-50",
    pattern: "bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-violet-100/40 via-violet-50/20 to-transparent",
    description: "Support athletes and teams"
  },
  { 
    title: "Creative", 
    image: "/assets/creative_cat.jpg",
    gradient: "from-sky-500 to-cyan-500",
    lightBg: "bg-sky-50",
    pattern: "bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-sky-100/40 via-sky-50/20 to-transparent",
    description: "Bring art and ideas to life"
  },
  { 
    title: "Current Events", 
    image: "/assets/current events_cat.png",
    gradient: "from-rose-500 to-pink-500",
    lightBg: "bg-rose-50",
    pattern: "bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-rose-100/40 via-rose-50/20 to-transparent",
    description: "Respond to what matters now"
  }
];

export default function Category() {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-card-reveal');
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-white py-24 md:py-28 lg:py-32 overflow-hidden">
      
      {/* Premium animated background - layered depth */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-50/80 via-white to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-gray-50/80 via-white to-transparent"></div>
        
        {/* Animated gradient orbs */}
        <div className="absolute top-20 -left-20 w-[500px] h-[500px] bg-gradient-to-r from-amber-200/20 to-orange-200/20 rounded-full blur-3xl animate-orb-slow"></div>
        <div className="absolute top-40 -right-20 w-[500px] h-[500px] bg-gradient-to-r from-teal-200/20 to-emerald-200/20 rounded-full blur-3xl animate-orb"></div>
        <div className="absolute -bottom-20 left-1/3 w-[600px] h-[600px] bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl animate-orb-slow"></div>
        <div className="absolute bottom-40 right-1/4 w-[400px] h-[400px] bg-gradient-to-r from-violet-200/20 to-purple-200/20 rounded-full blur-3xl animate-orb-delay"></div>
      </div>

      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 30 L30 30 Z' stroke='%2314b8a6' stroke-width='0.5'/%3E%3C/svg%3E")`,
        backgroundSize: '30px 30px'
      }}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header - Premium minimal */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/70 backdrop-blur-xl rounded-full border border-white/30 shadow-lg shadow-gray-200/20 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
            </span>
            <span className="text-sm font-medium bg-gradient-to-r from-gray-700 to-gray-600 bg-clip-text text-transparent">
              Discover causes
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
            Find your{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                purpose
              </span>
              <span className="absolute -bottom-3 left-0 w-full h-2 bg-gradient-to-r from-teal-200/50 to-emerald-200/50 blur-sm"></span>
              <span className="absolute -bottom-3 left-0 w-full h-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"></span>
            </span>
          </h2>
          
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Every cause tells a story. Every story needs a champion.
          </p>
        </div>

        {/* Categories Grid - 6 Premium Cards with All Original Images */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10 max-w-6xl mx-auto">
          {categories.map((item, index) => (
            <div
              key={item.title}
              ref={(el) => (cardRefs.current[index] = el)}
              className="group relative opacity-0 translate-y-10 transition-all duration-1000 cursor-pointer"
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              
              {/* Card container - with layered depth */}
              <div className="relative">
                
                {/* Animated glow ring - category specific */}
                <div className={`absolute -inset-3 bg-gradient-to-r ${item.gradient} rounded-[36px] blur-2xl opacity-0 group-hover:opacity-30 group-hover:scale-110 transition-all duration-700`}></div>
                
                {/* Main card - premium glass morphism */}
                <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl p-7 md:p-8 flex flex-col items-center border border-white/40 shadow-2xl shadow-gray-200/40 transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-3 group-hover:bg-white">
                  
                  {/* Animated pattern overlay */}
                  <div className={`absolute inset-0 ${item.pattern} opacity-0 group-hover:opacity-60 rounded-3xl transition-opacity duration-700`}></div>
                  
                  {/* Decorative top accent bar */}
                  <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r ${item.gradient} rounded-t-full group-hover:w-16 transition-all duration-500`}></div>
                  
                  {/* Image container - sculptural, artistic */}
                  <div className="relative mb-6 transform transition-all duration-700 group-hover:scale-110 group-hover:-translate-y-2">
                    
                    {/* Multi-layered background glow */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} rounded-3xl blur-xl opacity-0 group-hover:opacity-40 transition-all duration-700 scale-90 group-hover:scale-110`}></div>
                    <div className={`absolute inset-0 ${item.lightBg} rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-700 scale-95 group-hover:scale-105`}></div>
                    
                    {/* Image frame */}
                    <div className="relative w-28 h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 flex items-center justify-center p-3">
                      <div className="absolute inset-0 bg-white rounded-2xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <img
                        src={item.image}
                        alt={item.title}
                        className="relative w-full h-full object-contain drop-shadow-xl transition-all duration-700 group-hover:drop-shadow-2xl z-10"
                      />
                    </div>
                  </div>

                  {/* Title with animated underline */}
                  <div className="relative mb-3 text-center">
                    <h3 className={`text-xl md:text-2xl font-bold text-gray-800 mb-1 transition-all duration-300 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r ${item.gradient}`}>
                      {item.title}
                    </h3>
                    
                    {/* Animated underline */}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-teal-500 to-emerald-500 group-hover:w-12 transition-all duration-500"></div>
                  </div>
                  
                  {/* Description - subtle, emotional */}
                  <p className="text-sm text-gray-500 text-center leading-relaxed mb-5 group-hover:text-gray-600 transition-colors duration-300">
                    {item.description}
                  </p>
                  
                  {/* Interactive elements container */}
                  <div className="flex items-center justify-center gap-3 w-full mt-2">
                    
                    {/* Decorative dots */}
                    <div className="flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity`}></span>
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity delay-100`}></span>
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity delay-200`}></span>
                    </div>
                    
                    {/* Explore link - appears on hover */}
                    <div className="overflow-hidden">
                      <div className="flex items-center gap-2 translate-y-0 group-hover:translate-y-0 transition-transform duration-300">
                        <span className="text-xs font-medium text-gray-400 group-hover:text-gray-600 transition-colors">
                          Explore
                        </span>
                        <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Decorative dots */}
                    <div className="flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity delay-200`}></span>
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity delay-100`}></span>
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient} opacity-30 group-hover:opacity-60 transition-opacity`}></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Premium Call to Action */}
        <div className="text-center mt-20 md:mt-24">
          <div className="relative inline-flex flex-col items-center gap-6">
            
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 rounded-full blur-2xl opacity-70"></div>
            
            {/* Main CTA Button */}
            
            
            {/* Emotional footer text */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-teal-300 to-transparent"></div>
              <p className="text-sm text-gray-400 italic">
                Your kindness creates ripples
              </p>
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-teal-300 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes cardReveal {
          0% {
            opacity: 0;
            transform: translateY(40px) scale(0.96);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-card-reveal {
          animation: cardReveal 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        
        @keyframes orb {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
            opacity: 0.3;
          }
          25% { 
            transform: translate(30px, -20px) scale(1.1); 
            opacity: 0.4;
          }
          50% { 
            transform: translate(10px, 30px) scale(0.95); 
            opacity: 0.35;
          }
          75% { 
            transform: translate(-20px, 10px) scale(1.05); 
            opacity: 0.4;
          }
        }
        
        .animate-orb {
          animation: orb 24s ease-in-out infinite;
        }
        
        .animate-orb-slow {
          animation: orb 32s ease-in-out infinite reverse;
        }
        
        .animate-orb-delay {
          animation: orb 28s ease-in-out infinite 2s;
        }
      `}</style>
    </section>
  );
}